'use client';

import { CSSProperties, FC, useMemo } from 'react';
import classNames from 'classnames';
import { Container, CtaActionsRow, CtaButton } from '@/components';
import {
  alignContent,
  asText,
  bannerDefaultPrimaryFields,
  convertHexadecimalFormatToRGB,
  getSlicePadding,
  secondaryCtaFields,
  toCtaButtonPropsFromItem,
} from '@/utils';
import { BannerSlice, BannerSliceDefault } from '@/types';
import { BannerImages, getBannerSectionStyle } from './Banner.components';
import styles from './banner.module.scss';

const DefaultVariation: FC<BannerSlice & BannerSliceDefault> = slice => {
  const banner = slice.items[0];
  const primary = slice?.primary;
  const bannerActive = Boolean(banner?.active);
  const showMissingBannerPlaceholder = false;

  const primaryCta = useMemo(
    () =>
      banner
        ? toCtaButtonPropsFromItem(
            banner,
            bannerDefaultPrimaryFields,
            'primary',
          )
        : null,
    [banner],
  );
  const secondaryCta = useMemo(
    () =>
      banner
        ? toCtaButtonPropsFromItem(banner, secondaryCtaFields, 'secondary')
        : null,
    [banner],
  );

  if (!slice || !primary?.active) {
    return null;
  }

  const {
    background_color,
    bottom_spacing,
    full_width,
    adjust_aspect_ratio_desktop,
    adjust_aspect_ratio_mobile,
    max_height,
    max_height_mobile,
  } = primary;

  const title = asText(banner?.title);
  const description = asText(banner?.description);

  const background = banner?.background_color
    ? convertHexadecimalFormatToRGB(banner?.background_color)
    : null;

  const hasSideContentPadding =
    !full_width &&
    (banner?.horizontal_position === 'start' ||
      banner?.horizontal_position === 'end');

  const overlayBackground =
    banner?.background_color && background
      ? `rgba(${background.r}, ${background.g}, ${background.b}, ${banner?.opacity})`
      : undefined;

  const HeadingTag = banner?.title?.[0]?.type === 'heading1' ? 'h1' : 'h2';

  return (
    <section
      className={classNames(
        styles.page_banner__section,
        getSlicePadding(slice?.primary?.padding_section),
        {
          [styles.page_banner__section_fullwidth]: full_width,
          [styles.page_banner__section__spacing_bottom]: bottom_spacing,
        },
      )}
      style={
        getBannerSectionStyle(primary, {
          fullWidth: full_width,
          titleMaxWidth: banner?.title_max_width,
          titleMaxWidthMobile: banner?.title_max_width_mobile,
          descriptionMaxWidth: banner?.description_max_width,
          descriptionMaxWidthMobile: banner?.description_max_width_mobile,
        }) as CSSProperties
      }
    >
      <Container
        className={classNames(styles.page_banner__container, {
          [styles.page_banner__container_fullwidth]: full_width,
        })}
      >
        <BannerImages
          adjustAspectRatioDesktop={adjust_aspect_ratio_desktop}
          adjustAspectRatioMobile={adjust_aspect_ratio_mobile}
          banner={banner}
          bannerActive={bannerActive}
          maxHeightDesktop={max_height}
          maxHeightMobile={max_height_mobile}
          primaryBackgroundColor={background_color}
          showMissingBannerPlaceholder={showMissingBannerPlaceholder}
        />
        {bannerActive && (
          <span className={styles.overlay__container}>
            <span
              className={styles.overlay}
              style={
                overlayBackground
                  ? ({
                      '--banner-overlay-background': overlayBackground,
                    } as CSSProperties)
                  : undefined
              }
            />
          </span>
        )}

        {(description || title || primaryCta || secondaryCta) && (
          <Container
            className={classNames(styles.page__banner_body, {
              [styles.page__banner_body__center_mobile]:
                banner?.center_align_content_mobile,
            })}
            style={{
              alignItems: alignContent(banner?.horizontal_position),
              justifyContent: alignContent(
                banner?.vertical_position ?? 'center',
              ),
              maxWidth: banner?.content_width
                ? `${banner?.content_width}%`
                : undefined,
            }}
          >
            <div
              className={classNames(styles.page__banner_text, {
                [styles.text__align_centered_mobile]:
                  banner?.center_align_content_mobile,
                [styles.page__banner_text__side_padding]: hasSideContentPadding,
              })}
              style={{
                alignItems: alignContent(
                  banner?.horizontal_position ?? 'start',
                ),
                textAlign: banner?.text_alignment ?? 'left',
              }}
            >
              {title && (
                <HeadingTag
                  className={styles.page__banner_title}
                  dangerouslySetInnerHTML={{
                    __html: title,
                  }}
                  style={{
                    color: banner?.title_color as string,
                  }}
                />
              )}
              {description && (
                <p
                  className={styles.page__banner_description}
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                  style={{
                    color: banner?.description_color as string,
                  }}
                />
              )}
              {(primaryCta || secondaryCta) && (
                <CtaActionsRow className={styles.banner_content__actions}>
                  {primaryCta && (
                    <CtaButton
                      className={classNames(styles.banner_content__action, {
                        [styles.banner_content__action_hidde_mobile]:
                          banner?.hidden_button_mobile,
                      })}
                      {...primaryCta}
                    />
                  )}
                  {secondaryCta && (
                    <CtaButton
                      className={classNames(styles.banner_content__action, {
                        [styles.banner_content__action_hidde_mobile]:
                          banner?.hidden_button_mobile,
                      })}
                      {...secondaryCta}
                    />
                  )}
                </CtaActionsRow>
              )}
            </div>
          </Container>
        )}
      </Container>
    </section>
  );
};

export default DefaultVariation;
